/* 시공사례 편집 화면 — 위 막대(제목·저장·공개·다운로드·공유) · 왼쪽 탭 · 도구 막대 · 편집 화면 · 페이지 목록 · 오른쪽 AI 패널 */
import { IcAi, IcBack, IcDesign, IcDownload, IcElements, IcGlobe, IcLock, IcPhoto, IcText } from './icons';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Input, ItemList, ItemListRow, Page } from '@indipia/ui-core';
import { errorMessage } from '../api/showcaseApi';
import { buildBlogContent, writeBlogClipboard } from '../model/share';
import { makeZip, renderPages } from '../model/exporter';
import { newId, type El } from '../model/types';
import {
  clonePage, currentPage, deleteElement, duplicateSelected, ed, findElement, goHome, insertPageAfter, nextZ, rememberUndo, saveNow,
  setVisibility, toast, undoLast, useEd,
} from '../store/editor';
import { AiDock } from './AiDock';
import { Artboard } from './Artboard';
import { ContextBar } from './ContextBar';
import { MarkupModal } from './MarkupModal';
import { PageRail, PageStrip } from './Pages';
import { SidePanel, type PaneId } from './SidePanel';
import { cx, iconBtn, menuList, popHead, popNote, popover, rowHint, shake } from './ui';

const RAIL: { id: PaneId | 'ai'; icon: ReactNode; label: string }[] = [
  { id: 'photo', icon: <IcPhoto size={24} />, label: '사진' },
  { id: 'text', icon: <IcText size={24} />, label: '텍스트' },
  { id: 'elements', icon: <IcElements size={24} />, label: '요소' },
  { id: 'bg', icon: <IcDesign size={24} />, label: '디자인' },
  { id: 'ai', icon: <IcAi size={24} />, label: 'AI' },
];

let clip: { kind: 'page' | 'el'; data: unknown } | null = null;
const typingIn = () => {
  const ae = document.activeElement as HTMLElement | null;
  return !!ae && (ae.isContentEditable || ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA');
};

export function Editor({ onHome }: { onHome: () => void }) {
  const e = useEd();
  const [pane, setPaneRaw] = useState<PaneId>('photo');
  // 왼쪽 패널 — 켜져 있는 탭을 한 번 더 누르면 접힌다(오른쪽 AI 패널과 같게). 다른 탭이나 글꼴 고르기로 가면 다시 열린다
  const [sideOpen, setSideOpen] = useState(true);
  const setPane = (p: PaneId) => { setPaneRaw(p); setSideOpen(true); };
  const [markupEl, setMarkupEl] = useState<El | null>(null);
  // Modal 은 onClose 가 바뀌면 포커스를 첫 버튼으로 옮긴다 — 자동 저장으로 다시 그려도 같은 함수여야 요청 입력칸에서 포커스가 안 빠진다
  const closeMarkup = useCallback(() => setMarkupEl(null), []);
  const [dl, setDl] = useState<{ top: number; right: number } | null>(null);
  const [dlBusy, setDlBusy] = useState(false);
  const [nudge, setNudge] = useState(0);

  // 바뀐 게 있으면 2.5초마다 저장 (AI가 글을 쓰는 동안에도 중간중간), 페이지를 떠날 때도
  useEffect(() => {
    const t = setInterval(() => { void saveNow(); }, 2500);
    const hide = () => { void saveNow(false, true); };
    window.addEventListener('pagehide', hide);
    return () => { clearInterval(t); window.removeEventListener('pagehide', hide); void saveNow(); };
  }, []);

  // 단축키 — Ctrl+Z 되돌리기 · Delete 삭제 · Ctrl+D 복제 · Ctrl+C/V 요소(선택 없으면 페이지) 복사·붙여넣기 · Esc 요소 선택 끄기
  useEffect(() => {
    const key = (ev: KeyboardEvent) => {
      if (markupEl) return;
      const mod = ev.ctrlKey || ev.metaKey, k = ev.key.toLowerCase();
      if (ev.key === 'Escape' && ed.picking) { ed.picking = false; ed.bump(); return; }
      if (typingIn()) return; // 글을 고치는 중엔 브라우저 기본 동작
      if (mod && k === 'z' && !ev.shiftKey) { if (ed.busy) return; ev.preventDefault(); undoLast(); return; }
      if (ev.key === 'Delete' && ed.selectedId) { ev.preventDefault(); rememberUndo(); deleteElement(ed.selectedId); return; }
      if (mod && k === 'd' && ed.selectedId) { ev.preventDefault(); duplicateSelected(); return; }
      if (mod && (k === 'c' || k === 'v')) {
        if (String(window.getSelection() || '').length) return;
        if (k === 'c') {
          const el = findElement(ed.selectedId);
          clip = el ? { kind: 'el', data: JSON.parse(JSON.stringify(el)) } : { kind: 'page', data: JSON.parse(JSON.stringify(currentPage())) };
          toast(el ? '요소를 복사했어요 — Ctrl+V로 붙여넣기' : `${ed.cur + 1}페이지를 복사했어요 — Ctrl+V로 지금 페이지 뒤에 붙여넣기`);
          ev.preventDefault();
        } else if (clip) {
          ev.preventDefault();
          if (clip.kind === 'page') { insertPageAfter(clonePage(clip.data as never), ed.cur); toast('페이지를 붙여넣었어요'); }
          else {
            rememberUndo();
            const c = JSON.parse(JSON.stringify(clip.data)) as El;
            c.id = newId(); c.z = nextZ();
            if (currentPage().elements.some((o) => o.x === c.x && o.y === c.y && o.w === c.w)) { c.x = Math.min(700, c.x + 20); c.y = Math.min(940, c.y + 20); }
            delete c.origSrc;
            currentPage().elements.push(c);
            ed.selectedId = c.id;
            clip.data = JSON.parse(JSON.stringify(c)); // 또 붙이면 조금씩 비켜서
            ed.bump();
            toast('요소를 붙여넣었어요');
          }
        }
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [markupEl]);

  const openMarkup = (el: El) => {
    if (ed.busy) { toast('AI가 작업 중이에요. 끝나거나 ■ 중지한 뒤에 해 주세요.'); return; }
    setMarkupEl(el);
  };
  const share = async () => {
    await saveNow(true);
    if (ed.publicYn !== 'Y' || !ed.publicUrl) {
      toast('비공개 시공사례라 링크로 볼 수 없어요. "비공개" 버튼을 눌러 공개로 바꾸면 링크를 복사할 수 있어요.');
      setNudge((n) => n + 1);
      return;
    }
    try { await navigator.clipboard.writeText(ed.publicUrl); toast('공유 링크를 복사했어요'); }
    catch { window.prompt('이 주소를 복사하세요', ed.publicUrl); }
  };
  /* 네이버 블로그용 복사 — 페이지 사진(공개 주소로 올림) + 페이지 글. 클릭 순간에 클립보드 쓰기를 시작해야 브라우저가 허락하므로
     내용은 Promise 로 넘기고(사진 만들기·올리기가 끝나면 채워진다) 기다린다 */
  const copyBlog = () => {
    setDl(null);
    (document.activeElement as HTMLElement | null)?.blur?.(); // 고치던 글을 먼저 반영
    if (ed.publicYn !== 'Y' || !ed.projectId) {
      toast('블로그용 복사는 공개한 시공사례만 할 수 있어요. "비공개" 버튼을 눌러 공개로 바꾼 뒤 다시 해 주세요.');
      setNudge((n) => n + 1);
      return;
    }
    const id = ed.projectId;
    setDlBusy(true);
    const job = saveNow(true).then(() => buildBlogContent({ projectId: id, title: ed.title, pages: ed.pages }, toast));
    writeBlogClipboard(job)
      .then(() => toast('블로그용으로 복사했어요 — 네이버 블로그 글쓰기에서 Ctrl+V 로 붙여넣으세요'))
      .catch((err) => toast('블로그용으로 복사하지 못했어요: ' + errorMessage(err)))
      .finally(() => setDlBusy(false));
  };
  const download = async (mode: 'all' | 'one' | 'long') => {
    setDl(null);
    (document.activeElement as HTMLElement | null)?.blur?.(); // 고치던 글을 먼저 반영
    setDlBusy(true);
    toast(mode === 'all' ? `${ed.pages.length}페이지를 사진으로 만들고 있어요…` : '사진으로 만들고 있어요…');
    try {
      const title = (ed.title.trim() || '시공사례').replace(/[\\/:*?"<>|]/g, '_');
      const list = mode === 'one' ? [ed.pages[ed.cur]] : ed.pages;
      const files = await renderPages(list, mode);
      let blob: Blob, name: string;
      if (mode === 'all') { blob = await makeZip(files.map((f) => ({ name: `${title}_${f.name}.jpg`, blob: f.blob }))); name = `${title}.zip`; }
      else { blob = files[0].blob; name = mode === 'long' ? `${title}_긴이미지.jpg` : `${title}_${ed.cur + 1}페이지.jpg`; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast(`저장했어요 — ${name}`);
    } catch (err) {
      toast('사진으로 저장하지 못했어요: ' + errorMessage(err));
    } finally {
      setDlBusy(false);
    }
  };

  return (
    <Page layout="fixed" className="bg-surface">
      <div className="flex shrink-0 items-center gap-sm border-b border-border bg-surface-raised px-05 py-03">
        <Button size="sm" variant="ghost" className={iconBtn} aria-label="나의 시공사례로 돌아가기" title="나의 시공사례 (템플릿)" onClick={() => { if (goHome()) onHome(); }}><IcBack size={20} /></Button>
        <Input size="sm" className="w-[calc(var(--spacing-13)+var(--spacing-12))] [&_.input]:font-semibold" value={e.title} onChange={(ev) => { ed.title = ev.target.value; ed.bump(); }} aria-label="시공사례 제목" />
        <span className={cx('whitespace-nowrap text-caption', e.saveWarn ? 'font-semibold text-error' : 'text-text-secondary')} role="status">{e.saveWarn ? '⚠ ' : ''}{e.saveStatus}</span>
        <span className="flex-1" />
        <VisButton nudge={nudge} />
        <Button size="sm" variant="secondary" data-dl-btn disabled={dlBusy} aria-haspopup="menu" aria-expanded={!!dl} title="페이지를 사진(JPG)으로 저장 — 블로그·오늘의집에 올리기"
          onClick={(ev) => { if (dl) { setDl(null); return; } const r = ev.currentTarget.getBoundingClientRect(); setDl({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) }); }}>
          {dlBusy ? '사진 만드는 중…' : <><IcDownload size={16} />다운로드</>}
        </Button>
        <Button size="sm" title="공개한 시공사례의 링크를 복사해요" onClick={share}>공유하기</Button>
      </div>
      {dl && <DownloadMenu at={dl} onPick={download} onBlog={copyBlog} onClose={() => setDl(null)} />}
      <div className="flex min-h-0 flex-1">
        <nav className="flex w-nav-rail shrink-0 flex-col items-center gap-xs border-r border-border bg-surface-raised py-04" aria-label="편집 도구">
          {RAIL.map((r) => {
            const on = r.id === 'ai' ? e.dockOpen : sideOpen && (pane === r.id || (r.id === 'text' && pane === 'fonts'));
            return (
              <Button key={r.id} variant="ghost" className={cx('h-auto w-[calc(var(--layout-nav-rail)-var(--spacing-03))] flex-col gap-xs px-0 py-03 text-caption', on ? 'bg-group-subtle font-semibold text-group-onSubtle' : 'text-text-secondary')} aria-pressed={on}
                title={r.id === 'ai' ? '오른쪽 AI 패널 열기/닫기' : on ? `${r.label} 패널 접기` : undefined}
                onClick={() => { if (r.id === 'ai') { ed.dockOpen = !ed.dockOpen; ed.bump(); } else if (on) setSideOpen(false); else setPane(r.id); }}>
                {r.icon}<span>{r.label}</span>
              </Button>
            );
          })}
        </nav>
        <SidePanel pane={pane} setPane={setPane} open={sideOpen} />
        <div className="flex min-w-0 flex-1 flex-col">
          <ContextBar onFonts={() => setPane('fonts')} onMarkup={openMarkup} />
          <PageRail />
          <Artboard onMarkup={openMarkup} />
          <PageStrip />
        </div>
        <AiDock />
      </div>
      {e.picking && <div className="pointer-events-none fixed left-1/2 top-[calc(var(--layout-topbar-height)+var(--spacing-05))] z-tooltip -translate-x-1/2 rounded-full bg-[var(--shell-raised)] px-05 py-03 text-caption font-semibold text-[var(--gray-0)]" role="status">대화에 첨부할 요소를 클릭하세요 · Shift+클릭으로 여러 개 · Esc로 취소</div>}
      {markupEl && <MarkupModal el={markupEl} onClose={closeMarkup} />}
    </Page>
  );
}

function VisButton({ nudge }: { nudge: number }) {
  const e = useEd();
  const [busy, setBusy] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  // 공유하기를 눌렀는데 비공개면 흔들어서 알려준다
  useEffect(() => { if (nudge) shake(wrap.current, 2); }, [nudge]);
  const pub = e.publicYn === 'Y';
  return (
    <span ref={wrap} className="inline-flex">
    <Button size="sm" variant={pub ? 'secondary' : 'etc'} disabled={busy} aria-pressed={pub}
      title={pub ? '공개 중 — 링크가 있으면 누구나 볼 수 있어요. 누르면 비공개로' : '비공개 — 나만 볼 수 있어요. 누르면 공개로'}
      onClick={async () => { setBusy(true); await setVisibility(!pub); setBusy(false); }}>
      {busy ? '바꾸는 중…' : pub ? <><IcGlobe size={16} />공개</> : <><IcLock size={16} />비공개</>}
    </Button>
    </span>
  );
}

function DownloadMenu({ at, onPick, onBlog, onClose }: { at: { top: number; right: number }; onPick: (m: 'all' | 'one' | 'long') => void; onBlog: () => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const down = (ev: MouseEvent) => { const t = ev.target as HTMLElement; if (ref.current && !ref.current.contains(t) && !t.closest('[data-dl-btn]')) onClose(); };
    const key = (ev: KeyboardEvent) => { if (ev.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [onClose]);
  return (
    <div ref={ref} className={cx(popover, 'w-[calc(var(--spacing-13)*2+var(--spacing-05))]')} role="menu" style={{ top: at.top, right: at.right }}>
      <div className={popHead}><b>사진으로 저장</b><span className="text-caption text-text-secondary">JPG · 1080×1440 (3:4)</span></div>
      <ItemList className={menuList}>
        <ItemListRow role="menuitem" onClick={() => onPick('all')} title={`모든 페이지 (${ed.pages.length}장)`}
          hint={<span className={rowHint}>페이지마다 한 장씩, ZIP 하나로 — 블로그·오늘의집 "사진 여러 장 올리기"에 순서대로</span>} />
        <ItemListRow role="menuitem" onClick={() => onPick('one')} title="지금 페이지만" hint={<span className={rowHint}>{ed.cur + 1}페이지 한 장</span>} />
        <ItemListRow role="menuitem" onClick={() => onPick('long')} title="긴 이미지 한 장" hint={<span className={rowHint}>모든 페이지를 세로로 이어 붙인 한 장 — 블로그 본문에 통째로</span>} />
        <ItemListRow role="menuitem" onClick={onBlog} title="네이버 블로그용 복사"
          hint={<span className={rowHint}>맨 위 요약 글 → 템플릿 페이지 → 시공 사진 모아보기(모두 고화질 PNG) 순서로 복사 — 블로그 글쓰기에서 Ctrl+V. 공개한 시공사례만</span>} />
      </ItemList>
      <p className={popNote}>네이버 블로그: 사진 → 여러 장 선택 · 오늘의집: 집들이/노하우 글쓰기 → 사진 추가</p>
    </div>
  );
}
